import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { NavigationBar } from '@capgo/capacitor-navigation-bar';
import { useAuthActions } from '@convex-dev/auth/react';
import { useMediaQuery } from '@uidotdev/usehooks';
import { useConvexAuth, useQuery } from 'convex/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../convex/_generated/api';
import { AuthPage } from './app/login/page';
import { Home } from './app/tabs/home';
import { ReportEdit } from './app/tabs/reports/edit';
import { ReportLayout } from './app/tabs/reports/layout';
import { ReportsList } from './app/tabs/reports/list';
import { ReportProcessing } from './app/tabs/reports/processing';
import { ReportRecord } from './app/tabs/reports/record';
import { ReportRedirect } from './app/tabs/reports/redirect';
import { Settings } from './app/tabs/settings';
import { TemplateCreate } from './app/tabs/templates/create';
import { TemplateEdit } from './app/tabs/templates/edit';
import { TemplatesList } from './app/tabs/templates/list';
import { TemplateView } from './app/tabs/templates/view';
import { AppErrorBoundary } from './components/app-error-boundary';
import { AlertDialogProvider } from './components/common/alert-dialog-provider';
import { AuthCleanup } from './components/common/auth-cleanup';
import { FullscreenLoading } from './components/common/fullscreen-loading';
import { KeyboardAvoidingView } from './components/keyboard-avoiding-view';
import { AppLayout } from './components/layout/app-layout';
import { OnboardingFlow } from './components/onboarding/onboarding-flow';
import { SafeAreaProvider } from './components/safe-area-context';
import { Toaster } from './components/ui/sonner';
import { VersionGuard } from './components/version-guard';
import { AppTrackingTransparencyGuard } from './hooks/use-app-tracking-transparency';
import { useAutoUpdater } from './hooks/use-auto-updater';
import { useKeyboardListeners } from './hooks/use-keyboard-listeners';
import { AudioPlayerProvider } from './lib/audio/audio-player-provider';
import { RevenuecatProvider } from './lib/revenuecat/provider';
import { cn } from './lib/utils';

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <AppErrorBoundary />,
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: '/reports',
        children: [
          {
            index: true,
            element: <ReportsList />,
          },
          {
            path: ':id',
            element: <ReportLayout />,
            children: [
              {
                index: true,
                element: <ReportRedirect />,
              },
              {
                path: 'record',
                element: <ReportRecord />,
              },
              {
                path: 'processing',
                element: <ReportProcessing />,
              },
              {
                path: 'edit',
                element: <ReportEdit />,
              },
            ],
          },
        ],
      },
      {
        path: '/templates',
        children: [
          {
            index: true,
            element: <TemplatesList />,
          },
          {
            path: 'create',
            element: <TemplateCreate />,
          },
          {
            path: ':id',
            element: <TemplateEdit />,
          },
          {
            path: ':id/view',
            element: <TemplateView />,
          },
        ],
      },
      {
        path: '/settings',
        element: <Settings />,
      },
    ],
  },
]);

const authTransitionVariants = {
  initial: { opacity: 1 },
  animate: { opacity: 1 },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: 'easeIn' as const,
    },
  },
};

const appTransitionVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: 'easeOut' as const,
    },
  },
};

function App() {
  // Initialize keyboard listeners
  useKeyboardListeners();
  useAutoUpdater();
  const { isLoading: isAuthLoading, isAuthenticated } = useConvexAuth();
  const { signIn } = useAuthActions();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { t } = useTranslation('login');
  const [isVerifyingSignIn, setIsVerifyingSignIn] = useState(false);

  const userSettings = useQuery(
    api.userSettings.getSettings,
    isAuthenticated ? {} : 'skip',
  );

  const isLoading =
    isAuthLoading ||
    isVerifyingSignIn ||
    (isAuthenticated && userSettings === undefined);

  // Android: set navigation bar to white background with black buttons
  useEffect(() => {
    if (Capacitor.getPlatform() === 'android') {
      NavigationBar.setNavigationBarColor({
        color: 'transparent',
        darkButtons: true,
      }).catch(() => {
        // Plugin may not be available in some environments
        console.error('[Navbar] Failed to set navigation bar color');
      });
    }
  }, []);

  // Deep link listener
  useEffect(() => {
    const listener = CapApp.addListener('appUrlOpen', async ({ url }) => {
      if (url.startsWith('voicefill-medical://')) {
        const urlObj = new URL(url);
        const params = urlObj.searchParams;
        const code = params.get('code');

        if (code) {
          try {
            // Show fullscreen loading
            setIsVerifyingSignIn(true);

            const provider = urlObj.pathname.split('/').pop();
            if (provider && (provider === 'google' || provider === 'apple')) {
              await signIn(provider, { code });
            } else {
              throw new Error(`Unsupported provider: ${provider}`);
            }

            // Loading will be cleared automatically when auth state updates
          } catch (error) {
            // Hide loading on error
            setIsVerifyingSignIn(false);

            // Show error toast
            toast.error(t('signInFailed'));
            console.error('Failed to sign in with code', error);
          }
        }
      }
    });

    return () => {
      listener.then((handle) => handle.remove());
    };
  }, [signIn, t]);

  // Clear verifying state when authentication completes successfully
  useEffect(() => {
    if (isAuthenticated && isVerifyingSignIn) {
      setIsVerifyingSignIn(false);
    }
  }, [isAuthenticated, isVerifyingSignIn]);

  return (
    <SafeAreaProvider isEnabled={isMobile}>
      <VersionGuard>
        <AppTrackingTransparencyGuard>
          <AlertDialogProvider>
            <KeyboardAvoidingView enabled={isMobile}>
              <AnimatePresence mode="wait">
                {isLoading ? (
                  <FullscreenLoading isMobile={isMobile} />
                ) : isAuthenticated ? (
                  <RevenuecatProvider>
                    <AudioPlayerProvider>
                      {!userSettings?.hasCompletedOnboarding ? (
                        <OnboardingFlow />
                      ) : (
                        <motion.div
                          key="app-content"
                          variants={appTransitionVariants}
                          initial="initial"
                          animate="animate"
                          className="h-full w-full">
                          <AuthCleanup />
                          <RouterProvider router={router} />
                        </motion.div>
                      )}
                    </AudioPlayerProvider>
                  </RevenuecatProvider>
                ) : (
                  <motion.div
                    key="auth-content"
                    variants={authTransitionVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className={cn('h-full w-full', !isMobile && 'h-dvh')}>
                    <AuthPage />
                  </motion.div>
                )}
              </AnimatePresence>
              <Toaster />
            </KeyboardAvoidingView>
          </AlertDialogProvider>
        </AppTrackingTransparencyGuard>
      </VersionGuard>
    </SafeAreaProvider>
  );
}

export default App;
